import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    BasicProviderProps,
    FormRolType,
    NavItemDataProps,
    RolMenuContextFormType,
    RolesMenuModuleContextType,
    UserData,
    UserRolType
} from '../types'
import ErrorBoundary from '../components/error/ErrorBoundary'
import { DashboardContext } from './DashboardContext';
import { AppContext } from './AppContext';
import useDataClient from '../hooks/useDataClient';
import { rolesAdapter } from '../adapter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { URLAPP, URLQUERY } from '../config';

export const RolesMenuModuleContext = createContext<RolesMenuModuleContextType | null>(null);

export default function RolesMenuModuleProvider({ children }: BasicProviderProps) {
    const navigate = useNavigate();
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const [searchParams] = useSearchParams();
    const listUsersClient = useDataClient<UserData[]>({ appContext: appContext, initialTarget: rolesAdapter.getUsers() })
    const addRolClient = useDataClient<UserRolType[]>({ appContext })
    const updateRolClient = useDataClient<UserRolType[]>({ appContext })
    const newMenuClient = useDataClient<NavItemDataProps>({ appContext })
    const editMenuClient = useDataClient<NavItemDataProps>({ appContext })
    const statusMenuClient = useDataClient<NavItemDataProps>({ appContext })
    const deleteMenuClient = useDataClient<NavItemDataProps>({ appContext })
    const rolesData = dashboardContext?.appData.roles || []
    const resourcesData = dashboardContext?.appData.linkedResources || []
    const [rolTarget, setRolTarget] = useState<UserRolType | undefined>(dashboardContext?.appData.roles[0])
    const [menuTarget, setMenuTarget] = useState<NavItemDataProps | undefined>()
    const [showMenuForm, setShowMenuForm] = useState<RolMenuContextFormType>(null)
    const [showRolForm, setShowRolForm] = useState<FormRolType>()
    const userList = useMemo(() => (listUsersClient.status.data || []).sort((a, b) => a.name > b.name ? 1 : -1), [listUsersClient.status])
    const reloadResources = useCallback((message?: string) => {
        dashboardContext?.updateAppData('roles')
        setShowMenuForm(null)
        setShowRolForm(undefined)
        message && appContext?.setAppDialog({ message: message, type: 'success' })
    }, [])

    function controlMenuForm(value: RolMenuContextFormType, target?: NavItemDataProps) {
        setShowMenuForm(value)
        setMenuTarget(target)
    }

    function updateRol(rolData: UserRolType) {
        if (rolData.id === 0) {
            addRolClient.executeRequest(rolesAdapter.updateRol(rolData, 'add'))
        } else {
            updateRolClient.executeRequest(rolesAdapter.updateRol(rolData, 'update'))
        }
    }
    function addMenu(request: NavItemDataProps, idRol: number) {
        newMenuClient.executeRequest(rolesAdapter.addMenu(idRol, request))
    }
    function editMenu(request: NavItemDataProps) {
        editMenuClient.executeRequest(rolesAdapter.editMenu(request))
    }
    function changeStatusMenu(request: NavItemDataProps) {
        statusMenuClient.executeRequest(rolesAdapter.changeMenuStatus(request.id))
    }
    function deleteMenu(request: NavItemDataProps) {
        deleteMenuClient.executeRequest(rolesAdapter.deleteMenu(request.id))
    }

    function updateUsers() {
        listUsersClient.executeRequest()
    }

    const rolesModuleContext = useMemo<RolesMenuModuleContextType>(() => ({
        userList,
        rolTarget,
        resourcesData,
        rolesData,
        updateRol,
        showRolForm,
        setShowRolForm,
        menuTarget,
        setMenuTarget,
        showMenuForm,
        controlMenuForm,
        addMenu,
        editMenu,
        changeStatusMenu,
        deleteMenu,
        updateUsers
    }), [rolTarget, showRolForm, userList, rolesData, menuTarget, showMenuForm, resourcesData])

    useEffect(() => {
        let requestAddedId = newMenuClient.status.data
        if (requestAddedId) {
            reloadResources(`Menu agregado con id ${requestAddedId.id}`)
        }
    }, [newMenuClient.status.data])

    useEffect(() => {
        if (editMenuClient.status.data) {
            reloadResources(`Menu editado`)
        }
    }, [editMenuClient.status.data])

    useEffect(() => {
        if (statusMenuClient.status.data) {
            reloadResources()
        }
    }, [statusMenuClient.status.data])

    useEffect(() => {
        if (deleteMenuClient.status.data) {
            reloadResources()
        }
    }, [deleteMenuClient.status.data])

    useEffect(() => {
        if (!showMenuForm) {
            setMenuTarget(undefined)
        }
    }, [showMenuForm])

    useEffect(() => {
        if (updateRolClient.status.data) {
            reloadResources('Información actualizada')
        }
    }, [updateRolClient.status.data])


    useEffect(() => {
        if (addRolClient.status.data) {
            reloadResources('Información actualizada')
            navigate(`${URLAPP.accessMenu}${`?${URLQUERY.group}=${addRolClient.status.data.length}`}`)
        }
    }, [addRolClient.status.data])

    useEffect(() => {
        let target = searchParams.get(URLQUERY.group) || dashboardContext?.appData.roles[0]
        if (target) {
            setRolTarget(rolesData.find(r => r.id === Number(target)))
        }
    }, [searchParams, rolesData])

    return <ErrorBoundary>
        <RolesMenuModuleContext.Provider value={rolesModuleContext}>
            {children}
        </RolesMenuModuleContext.Provider>
    </ErrorBoundary>
}
